/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace dist
*/
var dist = {};

/**
* @name arcsine
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/arcsine}
*/
import arcsine from './../../../base/dists/arcsine';
setReadOnly( dist, 'arcsine', arcsine );

/**
* @name bernoulli
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/bernoulli}
*/
import bernoulli from './../../../base/dists/bernoulli';
setReadOnly( dist, 'bernoulli', bernoulli );

/**
* @name beta
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/beta}
*/
import beta from './../../../base/dists/beta';
setReadOnly( dist, 'beta', beta );

/**
* @name betaprime
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/betaprime}
*/
import betaprime from './../../../base/dists/betaprime';
setReadOnly( dist, 'betaprime', betaprime );

/**
* @name binomial
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/binomial}
*/
import binomial from './../../../base/dists/binomial';
setReadOnly( dist, 'binomial', binomial );

/**
* @name bradford
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/bradford}
*/
import bradford from './../../../base/dists/bradford';
setReadOnly( dist, 'bradford', bradford );

/**
* @name cauchy
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/cauchy}
*/
import cauchy from './../../../base/dists/cauchy';
setReadOnly( dist, 'cauchy', cauchy );

/**
* @name chi
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/chi}
*/
import chi from './../../../base/dists/chi';
setReadOnly( dist, 'chi', chi );

/**
* @name chisquare
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/chisquare}
*/
import chisquare from './../../../base/dists/chisquare';
setReadOnly( dist, 'chisquare', chisquare );

/**
* @name cosine
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/cosine}
*/
import cosine from './../../../base/dists/cosine';
setReadOnly( dist, 'cosine', cosine );

/**
* @name degenerate
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/degenerate}
*/
import degenerate from './../../../base/dists/degenerate';
setReadOnly( dist, 'degenerate', degenerate );

/**
* @name discreteUniform
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/discrete-uniform}
*/
import discreteUniform from './../../../base/dists/discrete-uniform';
setReadOnly( dist, 'discreteUniform', discreteUniform );

/**
* @name erlang
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/erlang}
*/
import erlang from './../../../base/dists/erlang';
setReadOnly( dist, 'erlang', erlang );

/**
* @name exponential
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/exponential}
*/
import exponential from './../../../base/dists/exponential';
setReadOnly( dist, 'exponential', exponential );

/**
* @name f
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/f}
*/
import f from './../../../base/dists/f';
setReadOnly( dist, 'f', f );

/**
* @name frechet
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/frechet}
*/
import frechet from './../../../base/dists/frechet';
setReadOnly( dist, 'frechet', frechet );

/**
* @name gamma
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/gamma}
*/
import gamma from './../../../base/dists/gamma';
setReadOnly( dist, 'gamma', gamma );

/**
* @name geometric
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/geometric}
*/
import geometric from './../../../base/dists/geometric';
setReadOnly( dist, 'geometric', geometric );

/**
* @name gumbel
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/gumbel}
*/
import gumbel from './../../../base/dists/gumbel';
setReadOnly( dist, 'gumbel', gumbel );

/**
* @name hypergeometric
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/hypergeometric}
*/

import hypergeometric from './../../../base/dists/hypergeometric';
setReadOnly( dist, 'hypergeometric', hypergeometric );

/**
* @name invgamma
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/invgamma}
*/
import invgamma from './../../../base/dists/invgamma';
setReadOnly( dist, 'invgamma', invgamma );

/**
* @name kumaraswamy
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/kumaraswamy}
*/
import kumaraswamy from './../../../base/dists/kumaraswamy';
setReadOnly( dist, 'kumaraswamy', kumaraswamy );

/**
* @name laplace
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/laplace}
*/
import laplace from './../../../base/dists/laplace';
setReadOnly( dist, 'laplace', laplace );

/**
* @name levy
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/levy}
*/
import levy from './../../../base/dists/levy';
setReadOnly( dist, 'levy', levy );

/**
* @name logistic
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/logistic}
*/
import logistic from './../../../base/dists/logistic';
setReadOnly( dist, 'logistic', logistic );

/**
* @name lognormal
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/lognormal}
*/
import lognormal from './../../../base/dists/lognormal';
setReadOnly( dist, 'lognormal', lognormal );

/**
* @name negativeBinomial
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/negative-binomial}
*/
import negativeBinomial from './../../../base/dists/negative-binomial';
setReadOnly( dist, 'negativeBinomial', negativeBinomial );

/**
* @name normal
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/normal}
*/
import normal from './../../../base/dists/normal';
setReadOnly( dist, 'normal', normal );

/**
* @name pareto1
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/pareto-type1}
*/
import pareto1 from './../../../base/dists/pareto-type1';
setReadOnly( dist, 'pareto1', pareto1 );

/**
* @name planck
* @memberof dist
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/planck}
*/
import planck from './../../../base/dists/planck';
setReadOnly( dist, 'planck', planck );

/**
* @name poisson
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/poisson}
*/
import poisson from './../../../base/dists/poisson';
setReadOnly( dist, 'poisson', poisson );

/**
* @name rayleigh
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/rayleigh}
*/
import rayleigh from './../../../base/dists/rayleigh';
setReadOnly( dist, 'rayleigh', rayleigh );

/**
* @name signrank
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/signrank}
*/
import signrank from './../../../base/dists/signrank';
setReadOnly( dist, 'signrank', signrank );

/**
* @name tukey
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/studentized-range}
*/
import tukey from './../../../base/dists/studentized-range';
setReadOnly( dist, 'tukey', tukey );

/**
* @name t
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/t}
*/
import t from './../../../base/dists/t';
setReadOnly( dist, 't', t );

/**
* @name triangular
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/triangular}
*/
import triangular from './../../../base/dists/triangular';
setReadOnly( dist, 'triangular', triangular );

/**
* @name truncatedNormal
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/truncated-normal}
*/
import truncatedNormal from './../../../base/dists/truncated-normal';
setReadOnly( dist, 'truncatedNormal', truncatedNormal );

/**
* @name uniform
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/uniform}
*/
import uniform from './../../../base/dists/uniform';
setReadOnly( dist, 'uniform', uniform );

/**
* @name weibull
* @memberof dist
* @readonly
* @type {Namespace}
* @see {@link module:@stdlib/stats/base/dists/weibull}
*/
import weibull from './../../../base/dists/weibull';
setReadOnly( dist, 'weibull', weibull );


// EXPORTS //

export default dist;
